import { useEffect, useState } from "react";

import { onIdle } from "~/tools/on-idle";

/**
 * Custom hook that returns a value of type T ones the browser is idle.
 * Returns undefined while the browser is busy.
 *
 * @template T - The type of the value returned by the factory function.
 * @param {function(prevValue: undefined | T): T} factory - The factory function that takes the previous value and returns a new value.
 * @param {ReadonlyArray<unknown>} dependencies - The dependencies array that triggers re-evaluation of the factory function.
 * @returns {undefined | T}
 */
function useIdle<T>(factory: (prevValue: undefined | T) => T, dependencies: ReadonlyArray<unknown>): undefined | T;

/**
 * Custom hook that returns an idle value based on the provided factory function, dependencies, and initial value.
 *
 * @template T - The type of the value returned by the factory function and the initial value.
 * @param {function} factory - The factory function that takes the previous value and returns a new value.
 * @param {ReadonlyArray<unknown>} dependencies - The dependencies array that triggers the factory function when changed.
 * @param {T} initial - The initial value to be used before the factory function is called.
 * @returns {T}
 */
function useIdle<T>(factory: (prevValue: T) => T, dependencies: ReadonlyArray<unknown>, initial: T): T;

function useIdle<T>(
	factory: (prevValue: undefined | T) => T,
	dependencies: ReadonlyArray<unknown>,
	initial?: T,
): undefined | T {
	const [value, setValue] = useState(initial);

	useEffect(
		() => onIdle(() => setValue(factory)),
		// biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are forwarded
		dependencies,
	);

	return value;
}

export { useIdle };
